import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format as formatDate } from 'date-fns';
import { clickPerformanceApi } from '../../services/api';

// The three funnel volume steps, widest first. Orders sit on their own right-hand axis —
// they are ~1-2 orders of magnitude smaller than overlays, so on a shared axis the line
// would be pinned flat to zero and its day-to-day movement unreadable.
const SERIES = [
  { key: 'overlays_shown', name: 'HandOff sizes shown', color: '#7e57c2', axis: 'left' },
  { key: 'clicks', name: 'Matches clicked', color: '#1976d2', axis: 'left' },
  { key: 'approved_orders', name: 'Orders', color: '#2e7d32', axis: 'right' },
];

const fmtInt = (v) => (v == null ? '—' : v.toLocaleString());

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>{label}</Typography>
      {SERIES.map((s) => (
        <Typography key={s.key} variant="body2" sx={{ color: s.color }}>
          {s.name}: {fmtInt(d?.[s.key])}
        </Typography>
      ))}
      {d?.overlays_shown > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {((d.clicks / d.overlays_shown) * 100).toFixed(1)}% of shown sizes clicked
        </Typography>
      )}
    </Paper>
  );
};

/**
 * Daily volume of the three funnel steps: HandOff sizes shown (overlays) → matches clicked
 * → orders. Levels, not rates: when orders fall, this separates "fewer shoppers saw a
 * handoff" (supply/overlap problem) from "they saw it and didn't click or buy" (a
 * conversion problem). The conversion-rate chart below cannot make that distinction,
 * because a drop in overlays leaves the rate unchanged.
 */
const ClickVolumeTrends = ({ fromDate, toDate, retailerId }) => {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [direction, setDirection] = useState('both');

  useEffect(() => {
    if (!retailerId) setDirection('both');
  }, [retailerId]);

  const fetchTrends = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await clickPerformanceApi.getTrends({
        from_date: fromDate,
        to_date: toDate,
        retailer_id: retailerId,
        direction,
      });
      if (result.success) {
        setTrends(result.data || []);
      } else {
        setError(result.error || 'Failed to load volume trends');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, retailerId, direction]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  const chartData = trends.map((t) => ({
    ...t,
    label: formatDate(new Date(t.date), 'MMM d'),
  }));

  // Overlays are recorded per reference (sending) retailer, so the series is absent when a
  // retailer is viewed purely as a receiver. Say so rather than drawing a gap.
  const overlaysMissing =
    chartData.length > 0 && chartData.every((d) => d.overlays_shown == null);

  const totals = chartData.reduce(
    (acc, d) => ({
      overlays_shown: acc.overlays_shown + (d.overlays_shown || 0),
      clicks: acc.clicks + (d.clicks || 0),
      approved_orders: acc.approved_orders + (d.approved_orders || 0),
    }),
    { overlays_shown: 0, clicks: 0, approved_orders: 0 },
  );

  const xInterval = chartData.length > 14 ? Math.floor(chartData.length / 10) : 0;

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 } }} elevation={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6">
            Funnel volume{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              (daily)
            </Typography>
          </Typography>
          {!loading && !error && chartData.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {totals.overlays_shown.toLocaleString()} sizes shown ·{' '}
              {totals.clicks.toLocaleString()} clicked · {totals.approved_orders.toLocaleString()} orders
            </Typography>
          )}
        </Box>
        {retailerId && (
          <ToggleButtonGroup
            value={direction}
            exclusive
            onChange={(_, v) => v && setDirection(v)}
            size="small"
          >
            <ToggleButton value="both">Both</ToggleButton>
            <ToggleButton value="sent">Sent</ToggleButton>
            <ToggleButton value="received">Received</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && trends.length === 0 && (
        <Typography color="text.secondary">No snapshot data for this period.</Typography>
      )}
      {!loading && !error && overlaysMissing && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Sizes shown are recorded for the retailer whose page displayed the overlay, so the
          series is only available in the “Sent” and “Both” views.
        </Alert>
      )}
      {!loading && !error && chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" interval={xInterval} tick={{ fontSize: 12 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 12 }} width={48} allowDecimals={false} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              width={40}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {SERIES.map((s) => (
              <Line
                key={s.key}
                yAxisId={s.axis}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

export default ClickVolumeTrends;
